// Distributed team footprint — list all five team-member cities explicitly so
// AI search and local directories place NTC inside the Portland metro, not just Newberg.
export const areaServed = [
  'Hillsboro, Oregon',
  'Tigard, Oregon',
  'Vancouver, Washington',
  'McMinnville, Oregon',
  'Newberg, Oregon',
  'Portland, Oregon',
  'Salem, Oregon',
  'Eugene, Oregon',
  'Yamhill County, Oregon',
  'Western Washington',
  'Idaho',
  'Pacific Northwest',
]

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://nexttechconsultants.com',
  name: 'NextTech Consultants',
  description: 'NextTech Consultants is a managed IT services and AI automation company with team members based across the Portland metro — Hillsboro, Tigard, Vancouver, McMinnville, and Newberg — serving 100+ businesses across Oregon, Washington, and Idaho.',
  url: 'https://nexttechconsultants.com',
  telephone: '+19712384801',
  email: 'info@nexttechconsultants.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Portland Metro',
    addressRegion: 'OR',
    addressCountry: 'US',
  },
  areaServed,
  serviceType: ['Managed IT Services', 'AI Business Automation', 'Cybersecurity', 'Cloud Migration'],
  priceRange: 'Starting at $219/seat/month',
  founder: { '@type': 'Person', name: 'Josiah Philipsen', jobTitle: 'Founder and Owner' },
  numberOfEmployees: { '@type': 'QuantitativeValue', value: 6 },
  foundingDate: '2015',
  slogan: 'Technology That Just Works. A Team Keeping You Ahead.',
  knowsAbout: [
    'Managed IT Services', 'AI Automation', 'HIPAA Compliance',
    'Microsoft 365', 'Google Workspace', 'AWS', 'Apple Business',
    'Cybersecurity for SMBs', 'Cloud Migration', 'Pacific Northwest IT Services',
    'SPECTRA Endpoint Security Certification',
    'Cybersecurity Certification for SMBs',
  ],
  hasCredential: {
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: 'Cybersecurity Certification',
    recognizedBy: {
      '@type': 'Organization',
      name: 'SPECTRA',
      url: 'https://spectracyber.com',
    },
  },
}

export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How much does managed IT services cost for a small business?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NextTech Consultants offers managed IT services starting at $219 per seat per month. This includes unlimited support, 24/7 monitoring, software licensing, and hardware procurement at cost — with no surprise fees.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does NextTech Consultants provide HIPAA-compliant IT services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. NextTech Consultants provides HIPAA-compliant managed IT infrastructure for medical practices and healthcare organizations throughout the Pacific Northwest.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is AI business automation for small businesses?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'AI business automation eliminates repetitive manual tasks like data entry, scheduling, report generation, and client intake. NTC audits your workflows and builds automations that run themselves, typically recovering 5-15 hours per employee per week.',
      },
    },
    {
      '@type': 'Question',
      name: 'What areas does NextTech Consultants serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NextTech Consultants is a distributed Pacific Northwest team with members in Hillsboro, Tigard, Vancouver, McMinnville, and Newberg, serving businesses throughout the Portland metro and Pacific Northwest, including Portland, Salem, Eugene, western Washington, and Idaho.',
      },
    },
  ],
}

// Services index page
export const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': 'https://nexttechconsultants.com/services',
  name: 'Managed IT Services & AI Automation',
  provider: {
    '@type': 'LocalBusiness',
    name: 'NextTech Consultants',
    url: 'https://nexttechconsultants.com',
  },
  areaServed,
  serviceType: ['Managed IT Services', 'AI Business Automation'],
  url: 'https://nexttechconsultants.com/services',
  description:
    'Full outsourced IT department and AI workflow automation for Pacific Northwest businesses.',
}

// About page
export const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': 'https://nexttechconsultants.com/about',
  name: 'About NextTech Consultants',
  url: 'https://nexttechconsultants.com/about',
  description:
    'Six-person team of certified IT professionals serving the Portland metro and Pacific Northwest, with team members in Hillsboro, Tigard, Vancouver, McMinnville, and Newberg. 11 years serving 100+ clients.',
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'NextTech Consultants',
    foundingDate: '2015',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 6 },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Portland Metro',
      addressRegion: 'OR',
      addressCountry: 'US',
    },
    founder: {
      '@type': 'Person',
      name: 'Josiah Philipsen',
      jobTitle: 'Founder and Owner',
    },
  },
}

// Pricing page
export const pricingSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://nexttechconsultants.com/pricing',
  name: 'Managed IT Pricing — Starting at $219/Seat/Month',
  url: 'https://nexttechconsultants.com/pricing',
  description:
    'Simple flat-rate pricing for managed IT services. No hourly billing, no surprise costs. Everything included.',
  mainEntity: {
    '@type': 'Offer',
    name: 'Managed IT Services',
    price: '219',
    priceCurrency: 'USD',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '219',
      priceCurrency: 'USD',
      unitText: 'per seat per month',
    },
    seller: {
      '@type': 'LocalBusiness',
      name: 'NextTech Consultants',
      url: 'https://nexttechconsultants.com',
    },
  },
}

// Chiropractic campaign landing
export const chiropracticSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://nexttechconsultants.com/chiropractic',
  name: 'HIPAA-Compliant IT for Chiropractic Practices',
  url: 'https://nexttechconsultants.com/chiropractic',
  description:
    'Managed IT built for chiropractic offices in Oregon and Washington. HIPAA compliance, EHR support, and cybersecurity — with $30,000 SPECTRA Care coverage. Free IT assessment for your practice.',
  mainEntity: {
    '@type': 'ProfessionalService',
    name: 'NextTech Consultants',
    url: 'https://nexttechconsultants.com',
    telephone: '+19712384801',
    email: 'info@nexttechconsultants.com',
    areaServed: ['Oregon', 'Washington', 'Pacific Northwest'],
    serviceType: ['HIPAA-Compliant Managed IT', 'EHR Support', 'Cybersecurity for Healthcare'],
  },
}

// Contact page
export const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': 'https://nexttechconsultants.com/contact',
  name: 'Contact NextTech Consultants',
  url: 'https://nexttechconsultants.com/contact',
  description:
    '30-minute discovery call, no obligation. Talk to Josiah about your tech challenges.',
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'NextTech Consultants',
    telephone: '+19712384801',
    email: 'info@nexttechconsultants.com',
    url: 'https://nexttechconsultants.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Portland Metro',
      addressRegion: 'OR',
      addressCountry: 'US',
    },
  },
}

// Security page
export const securitySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://nexttechconsultants.com/security',
  name: 'Cybersecurity Certification for Pacific Northwest Businesses',
  url: 'https://nexttechconsultants.com/security',
  description:
    'NextTech Consultants delivers SPECTRA-certified endpoint security for Oregon, Washington, and Idaho businesses. Third-party verified protection for legal, healthcare, and professional services firms.',
  mainEntity: {
    '@type': 'Service',
    name: 'SPECTRA Cybersecurity Certification',
    provider: { '@type': 'Organization', name: 'NextTech Consultants' },
    areaServed,
  },
}

// HUB Support form page
export const hubSupportSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://nexttechconsultants.com/hub-support',
  name: 'HUB Support — Report an Issue or Request an Enhancement',
  url: 'https://nexttechconsultants.com/hub-support',
  description:
    'Submit a bug report or enhancement request for the HUB EHR platform. NextTech Consultants support form.',
}

// Blog listing page
export const blogListSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': 'https://nexttechconsultants.com/blog',
  name: 'NextTech Consultants Blog',
  url: 'https://nexttechconsultants.com/blog',
  description:
    'Practical IT and AI automation guidance for Pacific Northwest SMBs — cybersecurity, managed IT, and AI workflow insights.',
  publisher: {
    '@type': 'LocalBusiness',
    name: 'NextTech Consultants',
    url: 'https://nexttechconsultants.com',
  },
}

export function blogPostSchema(post: {
  title: string
  description: string
  url: string
  datePublished: string
  dateModified: string
  authorName: string
}) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    url: post.url,
    author: { '@type': 'Person', name: post.authorName },
    publisher: {
      '@type': 'Organization',
      name: 'NextTech Consultants',
      url: 'https://nexttechconsultants.com',
    },
  }
  if (post.datePublished) schema.datePublished = post.datePublished
  if (post.dateModified) schema.dateModified = post.dateModified
  return schema
}
